import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  HeartPulse,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  useCreatePersonHealthProfile,
  useUpdatePersonHealthProfile,
  useDeletePersonHealthProfile,
} from '../hooks/PersonHealthProfile';
import { usePermissions } from '../hooks/usePermissions';
import { useGetPersonsPaged } from '../hooks/Person';
import { BasePerson } from '../dtos/Persons/index';
import {
  CreatePersonHealthProfile,
  UpdatePersonHealthProfile,
} from '../dtos/PersonHealthProfiles/index';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const healthProfileSchema = z.object({
  personId: z.string().min(1, 'Selectați o persoană'),
  isSmoker: z.boolean(),
  isFormerSmoker: z.boolean(),
  hasDiabetes: z.boolean(),
  hasHypertension: z.boolean(),
  hasDyslipidemia: z.boolean(),
  bodyMassIndex: z.coerce.number().optional(),
  systolicBloodPressure: z.coerce.number().optional(),
  familyHistoryCardiovascularDisease: z.boolean(),
});

type HealthProfileFormValues = z.infer<typeof healthProfileSchema>;

// Extended type for display - person with health profile
interface PersonWithHealthProfile {
  person: BasePerson;
  healthProfileId?: string;
}

export default function PersonHealthProfiles() {
  const { canCreate, canDelete } = usePermissions();
  const { canRead } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PersonWithHealthProfile | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  // Fetch persons with their health profiles using search endpoint
  const { data: personsResponse, isLoading } = useGetPersonsPaged(page, pageSize, searchTerm || undefined);
  const createMutation = useCreatePersonHealthProfile();
  const updateMutation = useUpdatePersonHealthProfile();
  const deleteMutation = useDeletePersonHealthProfile();

  const pagedData = personsResponse?.isSuccess ? personsResponse.data : null;
  const persons = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  // Filter persons that have health profiles
  const personsWithProfiles = useMemo(() => {
    return persons
      .filter((person) => person.healthProfile)
      .map((person) => ({
        person,
        healthProfileId: person.healthProfile?.id,
      }));
  }, [persons]);

  // Persons without health profiles (for create dialog)
  const personsWithoutProfiles = useMemo(() => {
    return persons.filter((person) => !person.healthProfile);
  }, [persons]);

  const updateParams = (updates: { page?: number; size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    if (updates.page !== undefined) {
      if (updates.page === 1) newParams.delete('page');
      else newParams.set('page', updates.page.toString());
    }
    if (updates.size !== undefined) {
      if (updates.size === 10) newParams.delete('size');
      else newParams.set('size', updates.size.toString());
    }
    if (updates.search !== undefined) {
      if (updates.search === '') newParams.delete('search');
      else newParams.set('search', updates.search);
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const handleSearch = () => updateParams({ search: searchInput });
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '' });
  };

  const createForm = useForm<HealthProfileFormValues>({
    resolver: zodResolver(healthProfileSchema),
    defaultValues: {
      personId: '',
      isSmoker: false,
      isFormerSmoker: false,
      hasDiabetes: false,
      hasHypertension: false,
      hasDyslipidemia: false,
      bodyMassIndex: undefined,
      systolicBloodPressure: undefined,
      familyHistoryCardiovascularDisease: false,
    },
  });

  const editForm = useForm<HealthProfileFormValues>({
    resolver: zodResolver(healthProfileSchema),
    defaultValues: {
      personId: '',
      isSmoker: false,
      isFormerSmoker: false,
      hasDiabetes: false,
      hasHypertension: false,
      hasDyslipidemia: false,
      bodyMassIndex: undefined,
      systolicBloodPressure: undefined,
      familyHistoryCardiovascularDisease: false,
    },
  });

  const handleCreate = async (values: HealthProfileFormValues) => {
    const data: CreatePersonHealthProfile = {
      personId: values.personId,
      isSmoker: values.isSmoker,
      isFormerSmoker: values.isFormerSmoker,
      hasDiabetes: values.hasDiabetes,
      hasHypertension: values.hasHypertension,
      hasDyslipidemia: values.hasDyslipidemia,
      bodyMassIndex: values.bodyMassIndex,
      systolicBloodPressure: values.systolicBloodPressure,
      familyHistoryCardiovascularDisease: values.familyHistoryCardiovascularDisease,
    };
    const res = await createMutation.mutateAsync(data);
    if (res.isSuccess) {
      setIsCreateDialogOpen(false);
      createForm.reset();
    }
  };

  const handleEdit = async (values: HealthProfileFormValues) => {
    if (!selectedItem?.healthProfileId) return;
    const data: Omit<UpdatePersonHealthProfile, 'id'> = {
      personId: values.personId,
      isSmoker: values.isSmoker,
      isFormerSmoker: values.isFormerSmoker,
      hasDiabetes: values.hasDiabetes,
      hasHypertension: values.hasHypertension,
      hasDyslipidemia: values.hasDyslipidemia,
      bodyMassIndex: values.bodyMassIndex,
      systolicBloodPressure: values.systolicBloodPressure,
      familyHistoryCardiovascularDisease: values.familyHistoryCardiovascularDisease,
    };
    const res = await updateMutation.mutateAsync({ id: selectedItem.healthProfileId, data });
    if (res.isSuccess) {
      setIsEditDialogOpen(false);
      setSelectedItem(null);
      editForm.reset();
    }
  };

  const handleDelete = async () => {
    if (!selectedItem?.healthProfileId) return;
    const res = await deleteMutation.mutateAsync(selectedItem.healthProfileId);
    if (res.isSuccess) {
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const openEditDialog = (item: PersonWithHealthProfile) => {
    setSelectedItem(item);
    const hp = item.person.healthProfile;
    editForm.reset({
      personId: item.person.id,
      isSmoker: hp?.isSmoker || false,
      isFormerSmoker: hp?.isFormerSmoker || false,
      hasDiabetes: hp?.hasDiabetes || false,
      hasHypertension: hp?.hasHypertension || false,
      hasDyslipidemia: hp?.hasDyslipidemia || false,
      bodyMassIndex: hp?.bodyMassIndex,
      systolicBloodPressure: hp?.systolicBloodPressure,
      familyHistoryCardiovascularDisease: hp?.familyHistoryCardiovascularDisease || false,
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (item: PersonWithHealthProfile) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  };

  const getPersonDisplayName = (person: BasePerson) => {
    const name = `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'N/A';
    return name;
  };

  const BoolBadge = ({ value, label }: { value: boolean; label: string }) =>
    value ? <Badge variant="default">{label}</Badge> : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile Sănătate</h1>
          <p className="text-muted-foreground mt-2">Gestionați profilurile de sănătate ale persoanelor.</p>
        </div>
        {canCreate() && (
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Adaugă Profil
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HeartPulse className="h-5 w-5" />
            Lista Profile Sănătate
          </CardTitle>
          <CardDescription>
            {personsWithProfiles.length} profile din {totalCount} persoane
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Caută persoane (nume, email, telefon, CNP...)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : personsWithProfiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <HeartPulse className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit persoane cu profile de sănătate.' : 'Nu există profile de sănătate.'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID Intern</TableHead>
                      <TableHead>Nume Persoană</TableHead>
                      <TableHead>IMC</TableHead>
                      <TableHead>TA Sistolică</TableHead>
                      <TableHead>Condiții</TableHead>
                      <TableHead className="w-[100px]">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {personsWithProfiles.map((item) => (
                      <TableRow key={item.person.id}>
                        <TableCell className="font-mono text-sm">
                          {item.person.personInternalId || '-'}
                        </TableCell>
                        <TableCell className="font-medium">
                          {getPersonDisplayName(item.person)}
                        </TableCell>
                        <TableCell>{item.person.healthProfile?.bodyMassIndex ?? '-'}</TableCell>
                        <TableCell>{item.person.healthProfile?.systolicBloodPressure ?? '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <BoolBadge value={item.person.healthProfile?.isSmoker || false} label="Fumător" />
                            <BoolBadge value={item.person.healthProfile?.isFormerSmoker || false} label="Fost Fumător" />
                            <BoolBadge value={item.person.healthProfile?.hasDiabetes || false} label="Diabet" />
                            <BoolBadge value={item.person.healthProfile?.hasHypertension || false} label="Hipertensiune" />
                            <BoolBadge value={item.person.healthProfile?.hasDyslipidemia || false} label="Dislipidemie" />
                            <BoolBadge value={item.person.healthProfile?.familyHistoryCardiovascularDisease || false} label="Istoric CV" />
                          </div>
                        </TableCell>
                        <TableCell>
                          {(canRead() || canDelete()) && (
                            <div className="flex items-center gap-1">
                              {canRead() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => openEditDialog(item)}
                                  title="Editează"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {canDelete() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => openDeleteDialog(item)}
                                  title="Șterge"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rânduri pe pagină:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => updateParams({ size: parseInt(value, 10), page: 1 })}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Pagina {page} din {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page - 1 })}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page + 1 })}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Adaugă Profil Sănătate
            </DialogTitle>
            <DialogDescription>Completați informațiile profilului de sănătate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit(handleCreate)}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Persoană *</Label>
                <Select
                  value={createForm.watch('personId')}
                  onValueChange={(value) => createForm.setValue('personId', value, { shouldValidate: true })}
                  disabled={personsWithoutProfiles.length === 0}
                >
                  <SelectTrigger className={createForm.formState.errors.personId ? 'border-destructive' : ''}>
                    <SelectValue placeholder={personsWithoutProfiles.length === 0 ? "Toate persoanele au deja profil" : "Selectați persoana"} />
                  </SelectTrigger>
                  <SelectContent>
                    {personsWithoutProfiles.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.personInternalId && `[${person.personInternalId}] `}
                        {person.firstName} {person.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {createForm.formState.errors.personId && (
                  <p className="text-sm text-destructive">{createForm.formState.errors.personId.message}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-bmi">IMC (Body Mass Index)</Label>
                  <Input id="create-bmi" type="number" step="0.1" {...createForm.register('bodyMassIndex')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-sbp">Tensiune Sistolică (mmHg)</Label>
                  <Input id="create-sbp" type="number" {...createForm.register('systolicBloodPressure')} />
                </div>
              </div>
              <div className="space-y-3">
                <Label>Condiții Medicale</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-smoker"
                      checked={createForm.watch('isSmoker')}
                      onCheckedChange={(checked) => createForm.setValue('isSmoker', !!checked)}
                    />
                    <Label htmlFor="create-smoker" className="text-sm font-normal">
                      Fumător
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-formerSmoker"
                      checked={createForm.watch('isFormerSmoker')}
                      onCheckedChange={(checked) => createForm.setValue('isFormerSmoker', !!checked)}
                    />
                    <Label htmlFor="create-formerSmoker" className="text-sm font-normal">
                      Fost Fumător
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-diabetes"
                      checked={createForm.watch('hasDiabetes')}
                      onCheckedChange={(checked) => createForm.setValue('hasDiabetes', !!checked)}
                    />
                    <Label htmlFor="create-diabetes" className="text-sm font-normal">
                      Diabet
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-hypertension"
                      checked={createForm.watch('hasHypertension')}
                      onCheckedChange={(checked) => createForm.setValue('hasHypertension', !!checked)}
                    />
                    <Label htmlFor="create-hypertension" className="text-sm font-normal">
                      Hipertensiune
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-dyslipidemia"
                      checked={createForm.watch('hasDyslipidemia')}
                      onCheckedChange={(checked) => createForm.setValue('hasDyslipidemia', !!checked)}
                    />
                    <Label htmlFor="create-dyslipidemia" className="text-sm font-normal">
                      Dislipidemie
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="create-cvHistory"
                      checked={createForm.watch('familyHistoryCardiovascularDisease')}
                      onCheckedChange={(checked) =>
                        createForm.setValue('familyHistoryCardiovascularDisease', !!checked)
                      }
                    />
                    <Label htmlFor="create-cvHistory" className="text-sm font-normal">
                      Istoric Cardiovascular Familial
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  createForm.reset();
                }}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Se creează...
                  </>
                ) : (
                  'Creează'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Editează Profil Sănătate
            </DialogTitle>
            <DialogDescription>Modificați informațiile profilului de sănătate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(handleEdit)}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Persoană</Label>
                <Input 
                  value={selectedItem ? `${selectedItem.person.personInternalId ? `[${selectedItem.person.personInternalId}] ` : ''}${getPersonDisplayName(selectedItem.person)}` : ''} 
                  disabled 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-bmi">IMC (Body Mass Index)</Label>
                  <Input id="edit-bmi" type="number" step="0.1" {...editForm.register('bodyMassIndex')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sbp">Tensiune Sistolică (mmHg)</Label>
                  <Input id="edit-sbp" type="number" {...editForm.register('systolicBloodPressure')} />
                </div>
              </div>
              <div className="space-y-3">
                <Label>Condiții Medicale</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-smoker"
                      checked={editForm.watch('isSmoker')}
                      onCheckedChange={(checked) => editForm.setValue('isSmoker', !!checked)}
                    />
                    <Label htmlFor="edit-smoker" className="text-sm font-normal">
                      Fumător
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-formerSmoker"
                      checked={editForm.watch('isFormerSmoker')}
                      onCheckedChange={(checked) => editForm.setValue('isFormerSmoker', !!checked)}
                    />
                    <Label htmlFor="edit-formerSmoker" className="text-sm font-normal">
                      Fost Fumător
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-diabetes"
                      checked={editForm.watch('hasDiabetes')}
                      onCheckedChange={(checked) => editForm.setValue('hasDiabetes', !!checked)}
                    />
                    <Label htmlFor="edit-diabetes" className="text-sm font-normal">
                      Diabet
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-hypertension"
                      checked={editForm.watch('hasHypertension')}
                      onCheckedChange={(checked) => editForm.setValue('hasHypertension', !!checked)}
                    />
                    <Label htmlFor="edit-hypertension" className="text-sm font-normal">
                      Hipertensiune
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-dyslipidemia"
                      checked={editForm.watch('hasDyslipidemia')}
                      onCheckedChange={(checked) => editForm.setValue('hasDyslipidemia', !!checked)}
                    />
                    <Label htmlFor="edit-dyslipidemia" className="text-sm font-normal">
                      Dislipidemie
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="edit-cvHistory"
                      checked={editForm.watch('familyHistoryCardiovascularDisease')}
                      onCheckedChange={(checked) =>
                        editForm.setValue('familyHistoryCardiovascularDisease', !!checked)
                      }
                    />
                    <Label htmlFor="edit-cvHistory" className="text-sm font-normal">
                      Istoric Cardiovascular Familial
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedItem(null);
                  editForm.reset();
                }}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  'Salvează'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți profilul de sănătate pentru{' '}
              <strong>
                {selectedItem?.person.personInternalId && `[${selectedItem.person.personInternalId}] `}
                {selectedItem ? getPersonDisplayName(selectedItem.person) : ''}
              </strong>
              ? Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedItem(null)}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Se șterge...
                </>
              ) : (
                'Șterge'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
